<template>
  <div class="variants__wrapper">
    <p
      class="variants__label"
      :class="{
        'variants__label--sold-out': isSoldOut,
      }"
    >
      Size: <span>{{ variantStatus }}</span>
    </p>
    <div class="variants">
      <Button
        v-for="variant in variants"
        :key="variant.id"
        width="full"
        theme="secondary"
        form="outline"
        class="variant"
        :class="{
          'variant--disabled': variantDisabled(variant),
          'variant--selected': selectedVariant?.id === variant.id,
        }"
        @click="handleVariantSelect(variant)"
        @mouseenter="hoveredVariant = variant"
        @mouseleave="hoveredVariant = null"
      >
        {{ variant.title }}
      </Button>
    </div>
    <VariantsMeta v-if="!hideMedia" />
  </div>
</template>

<script>
// Import components
import Button from "@/components/ButtonV2.vue";
import VariantsMeta from "@/components/product-display/VariantsMeta.vue";

// Import internal modules
import { mapState, mapActions, mapGetters } from "vuex";

export default {
  name: "Variants",
  components: {
    Button,
    VariantsMeta,
  },
  props: {
    hideMedia: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      hoveredVariant: null,
    };
  },
  computed: {
    ...mapState("product", ["product", "selectedVariant"]),
    ...mapGetters("product", ["variants"]),
    variantStatus() {
      return this.hoveredVariant && this.quantityStatus
        ? this.hoveredVariant.title + " " + this.quantityStatus
        : null;
    },
    quantityStatus() {
      const quantity = this.hoveredVariant?.quantityAvailable;

      switch (true) {
        case quantity <= 0 && this.availableForPreOrder(this.hoveredVariant):
          return "Available for preorder";
        case quantity <= 0:
          return "Out of stock";
        case quantity <= 15 && quantity > 5:
          return "Low stock";
        default:
          return null;
      }
    },
    isSoldOut() {
      if (
        !this.hoveredVariant ||
        this.availableForPreOrder(this.hoveredVariant)
      ) {
        return false;
      }

      return (
        !this.hoveredVariant.availableForSale ||
        this.hoveredVariant.quantityAvailable <= 0
      );
    },
  },
  methods: {
    ...mapActions("product", ["setSelectedVariant", "updateProductUrlV2"]),
    handleVariantSelect(variant) {
      this.setSelectedVariant(variant);
      this.updateProductUrlV2();
    },
    variantDisabled(variant) {
      const variantIsPreOrder = this.availableForPreOrder(variant);

      if (variantIsPreOrder) {
        return false;
      }

      return !variant.availableForSale || variant.quantityAvailable <= 0;
    },
    preOrder(variant) {
      const preorder = variant?.randbPreorder?.value;
      if (!preorder) return false;

      return JSON.parse(preorder);
    },
    availableForPreOrder(variant) {
      if (!this.preOrder) return false;

      const { limit = 0, reserved = 0 } = this.preOrder(variant);
      const inventory = variant.quantityAvailable || 0;

      return inventory <= 0 && reserved < limit;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/components/form";

.variants {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
  gap: 10px;
  justify-items: flex-start;
  align-items: center;

  & > * {
    display: block;
    width: 100%;
    word-wrap: break-word;
  }
}

.variants__wrapper {
  margin-top: 24px;
}

.variant {
  padding-left: 0;
  padding-right: 0;

  &--disabled {
    background-color: $color-grey-light;
    border-color: $color-grey-light;
    color: $color-grey-dark;

    &:hover {
      background-color: $color-grey-light;
      border-color: $color-grey-light;
      color: $color-grey-dark;
    }
  }

  &--selected {
    border-color: $color-grey-dark;
  }
}

.variants__label {
  @include utility-label(14px);
  margin-bottom: 8px;

  &--sold-out span {
    color: $color-red;
  }
}
</style>
